// Signing Cinema Roll into Movie Hat.
//
// Movie Hat is a different Firebase project, so a Cinema Roll ID token means
// nothing to it. Once Movie Hat's database rules require a signed-in member —
// which is the whole point of locking it down — Cinema Roll has to hold its
// own session there or every hat request is refused.
//
// So: a SECOND, named Firebase app pointing at Movie Hat's project, with its
// own Google sign-in. Matt, 2026-08-16, asked for exactly this ("second sign
// in in settings is fine"). One popup, once, and after that the session
// persists like any other.
//
// A named app keeps the two completely independent: signing into Movie Hat
// cannot disturb the Cinema Roll session, and signing out of Cinema Roll
// cannot disturb this one.
//
// The config below is Movie Hat's public web config, already shipped inside
// its own bundle — there is nothing secret in it.
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithCustomToken, signOut, onAuthStateChanged } from 'firebase/auth';

const APP_NAME = 'movieHat';

const MOVIE_HAT_CONFIG = {
  apiKey: 'AIzaSyDlfyRC1BgoQ6UCPKsX-dvFC9HumeEwGjg',
  authDomain: 'movie-hat-9c418.firebaseapp.com',
  databaseURL: 'https://movie-hat-9c418-default-rtdb.firebaseio.com',
  projectId: 'movie-hat-9c418',
  storageBucket: 'movie-hat-9c418.appspot.com',
  messagingSenderId: '1061874698443',
  appId: '1:1061874698443:web:b2326dbc709a9237c2b34e'
};

/** Created on first use, never at import: nobody who ignores hats should pay for it. */
function movieHatApp () {
  const existing = getApps().find((app) => app.name === APP_NAME);
  return existing || initializeApp(MOVIE_HAT_CONFIG, APP_NAME);
}

export function movieHatAuth () {
  return getAuth(movieHatApp());
}

/** The signed-in Movie Hat account, or null. */
export function movieHatUser () {
  try {
    return movieHatAuth().currentUser;
  } catch {
    return null;
  }
}

// Firebase restores a persisted session ASYNCHRONOUSLY, so `currentUser` is
// null for the first few hundred milliseconds after a page load even for
// somebody who is connected. Anything asking for a token in that window gets
// none and is refused — which took Movie Hat down on 2026-08-17.
let firstAuthResult = null;

function movieHatAuthReady () {
  if (!firstAuthResult) {
    firstAuthResult = new Promise((resolve) => {
      try {
        const unsubscribe = onAuthStateChanged(
          movieHatAuth(),
          (user) => { unsubscribe(); resolve(user || null); },
          () => resolve(null)
        );
      } catch {
        resolve(null);
      }
    });
  }
  return firstAuthResult;
}

/**
 * The ID token for Movie Hat requests, or null when not connected.
 *
 * Asked for per request rather than held, because getIdToken refreshes
 * itself as the hour-long token nears expiry.
 */
export async function movieHatToken () {
  return (await movieHatSession()).token;
}

/**
 * The session behind a request: `{ token, email, reason }`.
 *
 * `reason` is null when there's a usable token, and otherwise says WHICH
 * failure this is:
 *
 *   'not-connected' — nobody has signed in to Movie Hat on this device.
 *   'token-failed'  — there is a session, but the token couldn't be minted
 *                     (a revoked refresh token, or simply being offline).
 *
 * These used to collapse into a bare `null`, which every caller then turned
 * into an unexplained 401 — and a 401 that tells nobody anything is what left
 * the hats section dead for days with no way to tell a lapsed session apart
 * from being signed in as the wrong account. `email` rides along so the UI
 * can name the account that's actually connected.
 */
export async function movieHatSession () {
  // Wait for the session to be restored before concluding there isn't
  // one — but only ever as a "has auth settled yet" gate. The cached
  // first result goes stale the moment somebody disconnects or connects
  // a different account, so the user always comes from currentUser.
  let user;
  try {
    user = movieHatUser();
    if (!user) {
      await movieHatAuthReady();
      user = movieHatUser();
    }
  } catch (error) {
    console.warn('Could not reach the Movie Hat session', error);
    return { token: null, email: null, reason: 'not-connected' };
  }

  if (!user) return { token: null, email: null, reason: 'not-connected' };

  try {
    return { token: await user.getIdToken(), email: user.email || null, reason: null };
  } catch (error) {
    // Deliberately NOT 'not-connected': a signed-in account whose token can't
    // be refreshed needs re-connecting, and telling someone they were never
    // signed in when they plainly were sends them looking in the wrong place.
    console.warn('Could not get a Movie Hat token', error);
    return { token: null, email: user.email || null, reason: 'token-failed' };
  }
}

/** Calls back with the Movie Hat user (or null) whenever it changes. */
export function watchMovieHatAuth (callback) {
  try {
    return onAuthStateChanged(movieHatAuth(), callback);
  } catch {
    callback(null);
    return () => {};
  }
}

/**
 * The one popup. Sign in with the same Google account that owns your hats —
 * the address is what Movie Hat's membership is keyed by.
 */
export async function connectMovieHat () {
  const provider = new GoogleAuthProvider();
  // Always offer the chooser: the Google account that owns your hats may not
  // be the one you're signed into Cinema Roll with.
  provider.setCustomParameters({ prompt: 'select_account' });

  const result = await signInWithPopup(movieHatAuth(), provider);
  return result?.user || null;
}

/**
 * Sign in with a minted custom token instead of the Google popup.
 *
 * Since the lockdown, anything touching a hat needs a real signed-in member,
 * and an automated browser cannot complete a Google popup — so verifying the
 * integration at all needs this door. `yarn mint-hat-token` in the movie-hat
 * repo produces the token and grants the account the Dev Hat, and nothing
 * else: the rules stop it reading any other hat.
 *
 * Same reasoning as Cinema Roll's own `?testToken=` sign-in hook.
 */
export async function connectMovieHatWithToken (token) {
  const result = await signInWithCustomToken(movieHatAuth(), token);
  return result?.user || null;
}

export function disconnectMovieHat () {
  return signOut(movieHatAuth());
}

/**
 * An email address as Movie Hat keys it.
 *
 * MIRRORS src/store/memberKey.mjs in the movie-hat repo, which is the source
 * of truth and also generates the database rules from the same list. If that
 * changes, this has to change with it.
 */
const UNSAFE_KEY_CHARACTERS = ['.', '$', '#', '[', ']', '/'];

export function emailToMemberKey (email) {
  if (typeof email !== 'string' || !email) return null;

  return UNSAFE_KEY_CHARACTERS.reduce(
    (key, character) => key.split(character).join('-'),
    email.trim().toLowerCase()
  );
}
